import { useMemo } from 'react';

import {
  ChartMetricsRow,
  ChartMetricsRowSkeleton,
} from '@cast/features-cluster-ui';

import { InventoryResourceMetricBox } from '../../../../_components/ui/InventoryResourceMetricBox';
import {
  ReliabilityMetricConfig,
  ReliabilityMetricKey,
  ReliabilityMetricsData,
} from '@cast/split-me/features/cluster/_components/reliability';

type ReliabilityMetricsSummaryProps = {
  metrics: ReliabilityMetricConfig[];
  data?: ReliabilityMetricsData;
  visibleMetrics: Record<string, boolean>;
  toggleMetric: (key: string) => void;
  testId?: string;
  isLoading?: boolean;
  formatValue: (
    summary: number | undefined,
    metric: ReliabilityMetricConfig
  ) => { value: number | null | undefined; unit?: string };
};

export const ReliabilityMetricsSummary = ({
  metrics,
  data,
  visibleMetrics,
  toggleMetric,
  testId,
  isLoading = false,
  formatValue,
}: ReliabilityMetricsSummaryProps) => {
  const colorMap = useMemo(
    () =>
      Object.fromEntries(
        metrics.map((metric) => [metric.key, metric.stroke])
      ) as Record<ReliabilityMetricKey, string | undefined>,
    [metrics]
  );
  const titleMap = useMemo(
    () =>
      Object.fromEntries(
        metrics.map((metric) => [metric.key, metric.label])
      ) as Record<ReliabilityMetricKey, string>,
    [metrics]
  );

  if (isLoading) {
    return (
      <ChartMetricsRowSkeleton
        alwaysRow
        gap={16}
        count={metrics.length}
        testId={testId ? `${testId}-metrics-skeleton` : undefined}
      />
    );
  }

  return (
    <ChartMetricsRow alwaysRow alignItems="center" gap={16} testId={testId}>
      {metrics.map((metric) => {
        const summary = data?.[metric.key]?.summary;
        const { value, unit } = formatValue(summary, metric);

        return (
          <InventoryResourceMetricBox
            key={metric.key}
            testId={`${testId}-${metric.key}-summary`}
            dataKey={metric.key}
            value={value}
            unit={unit}
            label={metric.label}
            colorMap={colorMap}
            titleMap={titleMap}
            toggleMetric={toggleMetric}
            isMetricDisabled={(key) => !visibleMetrics[key]}
            size="xs"
          />
        );
      })}
    </ChartMetricsRow>
  );
};
